import {Typography} from '@mui/material';
import {DateUtils, PopupWrapper} from '@wallaby-ui/core';
import {MerieSchema, useFormatters} from '@wallaby-ui/document';
import React, {useCallback, useMemo, useState} from 'react';

type PhRecommendations = MerieSchema.components['schemas']['PharmaRecommendationsDto']['pharmaRecommendations'] | undefined;
type PharmaRecommendationCreateUpdateDto = MerieSchema.components['schemas']['PharmaRecommendationCreateUpdateDto'];

type PastUncheckedPhRecUpdaterParams = {
  items: PhRecommendations;
  updatePhRec: (phRec: PharmaRecommendationCreateUpdateDto) => void;
  pharmaRecommendationsRefetch: () => void;
};

export function usePastUncheckedPhRecUpdater({items, updatePhRec, pharmaRecommendationsRefetch}: PastUncheckedPhRecUpdaterParams) {
  const [anchorTag, setAnchorTag] = useState<HTMLElement | null>(null);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const {t} = useFormatters();

  const checkPhRecChecked = useCallback(
    (action: () => void, targetElement?: HTMLElement) => {
      const uncheckedPhRecCount = items?.filter((item) => !item.checked && item.followUpDate)?.length ?? 0;
      if (uncheckedPhRecCount === 0) {
        action();
      } else {
        setPendingAction(() => action);
        setAnchorTag(targetElement || null);
      }
    },
    [items]
  );

  const callPendingActionAndClean = useCallback(() => {
    pendingAction?.();
    setAnchorTag(null);
    setPendingAction(null);
  }, [pendingAction]);

  const handleConfirm = useCallback(async () => {
    if (items) {
      await Promise.all(
        items.map((item) => {
          const newItem = {...item};
          newItem.checked = true;
          if (!newItem.followUpDate) {
            newItem.followUpDate = DateUtils.toIsoDateString(new Date());
          }
          return updatePhRec(newItem);
        })
      );
      pharmaRecommendationsRefetch?.();
    }
    callPendingActionAndClean();
  }, [items, callPendingActionAndClean, updatePhRec, pharmaRecommendationsRefetch]);

  const handleCancel = useCallback(() => {
    callPendingActionAndClean();
  }, [callPendingActionAndClean]);

  const modal = useMemo(() => {
    if (!anchorTag || !pendingAction) return null;

    return (
      <PopupWrapper
        openButton={false}
        anchorTag={anchorTag}
        direction='down'
        yOffset={4}
        showDiscardSaveButtons
        cancelTitle='global.no'
        confirmTitle='global.yes'
        onClose={handleCancel}
        onConfirm={handleConfirm}
      >
        <Typography variant='sectionHeader'>{t('hospitalization.detail.phRec.addNew.updateChecked')}</Typography>
      </PopupWrapper>
    );
  }, [anchorTag, pendingAction, handleCancel, handleConfirm, t]);

  return {checkPhRecChecked, modal};
}
